import { AB_TEST, AB_TEST_CONFIG_MAP, FEATURE_FLAG, TRUE_VALUE } from '../apis/eols-features-api/eols-features-api.constants';
import { FeatureFlagDto, FeatureFlagsGroupedDto } from '../apis/eols-features-api/eols-features-api.dtos';
import { Application } from '../apis/eols-app-link/eols-app-link.constants';

const isGlobalFeatureFlag = (flag: FeatureFlagsGroupedDto): boolean => {
  return !flag.groups?.length || flag.groups.includes('ALL');
};

export const getGroupFeatureFlagWithFallbackToGlobal = (flags: FeatureFlagsGroupedDto[], featureFlag: FEATURE_FLAG, group: string): FeatureFlagsGroupedDto => {

  if (!flags) {
    return null;
  }

  const groupMatch = flags.find((flag) => {
    return flag.featureName === featureFlag && group && flag.groups?.includes(group);
  });
  if (groupMatch) {
    return groupMatch;
  }
  return flags.find((flag) => {
    return flag.featureName === featureFlag && isGlobalFeatureFlag(flag);
  });
};

export const getBooleanFromGroupFeatureFlagWithFallbackToGlobal = (flags: FeatureFlagsGroupedDto[], featureFlag: FEATURE_FLAG, group: string): boolean => {
  let singleAppFlags = flags;
  const hasAmaFlags = flags?.some(flag => flag.eolsApp === Application.ASSIGNMENT_MANAGEMENT);
  const hasOtherAppFlags = flags?.some(flag => flag.eolsApp && flag.eolsApp !== Application.ASSIGNMENT_MANAGEMENT);
  const hasMultipleAppFlags = hasAmaFlags && hasOtherAppFlags;

  if (hasMultipleAppFlags) {
    singleAppFlags = flags.filter(flag => flag.eolsApp === Application.ASSIGNMENT_MANAGEMENT);
  }

  const flag = getGroupFeatureFlagWithFallbackToGlobal(singleAppFlags, featureFlag, group);
  if (!flag) {
    // Return null here to force explicit setting of feature flags
    return null;
  }
  return flag.featureValue === TRUE_VALUE;
};

export const getFeatureFlagGroups = (flags: FeatureFlagsGroupedDto[], featureName: FEATURE_FLAG, featureValue: string = TRUE_VALUE): string[] => {

  if (!flags?.length) {
    return [];
  }

  const flag = flags.find((featureFlag) => {
    return featureFlag.featureName === featureName
      && featureFlag.featureValue === featureValue
      && featureFlag.groups?.length;
  });

  if (!flag?.groups) {
    return [];
  }

  return flag.groups;
};

export const getRandomABTestFlavor = (flag: FeatureFlagsGroupedDto, rand = Math.random()) => {
  const possibleFlavors = AB_TEST_CONFIG_MAP[flag.featureName];
  if (!possibleFlavors?.length) {
    return null;
  }
  return possibleFlavors[Math.floor(rand * possibleFlavors.length)];
};

export const getABTestFlavor = (props: {
  flag: FEATURE_FLAG;
  appFlags: FeatureFlagsGroupedDto[];
  abFlags: FeatureFlagDto[];
  group: string;
  defaultFlavor: string;
}): string => {

  const {
    flag,
    appFlags,
    abFlags,
    group,
    defaultFlavor
  } = props;

  const appFlag = getGroupFeatureFlagWithFallbackToGlobal(appFlags, flag, group);
  if (appFlag && appFlag.featureValue !== AB_TEST) {
    return appFlag.featureValue;
  }
  const abFlag = abFlags.find((_abFlag) => {
    return _abFlag.featureName === flag;
  });

  if (abFlag) {
    return abFlag.featureValue;
  }
  return defaultFlavor;
};

export const getABPropsForAnalytics = (abTestFlavors: FeatureFlagDto[]): Record<string, string> => {
  if (!abTestFlavors?.length) {
    return {};
  }
  return abTestFlavors.reduce((acc, cur) => {
    return {
      ...acc,
      [cur.featureName]: cur.featureValue
    };
  }, {});
};

export const getActiveABTestFlags = (flags: FeatureFlagsGroupedDto[], group: string): FeatureFlagsGroupedDto[] => {

  if (!flags) {
    return null;
  }

  const groupMatches = flags.filter((flag) => {
    return flag.featureValue === AB_TEST && group && flag.groups.includes(group);
  });

  const globalMatches = flags.filter((flag) => {
    if (
      groupMatches.some((_flag) => {
        return _flag.featureName === flag.featureName;
      })
    ) {
      return false;
    }
    return flag.featureValue === AB_TEST && flag.groups.length === 0;
  });

  return [...groupMatches, ...globalMatches];
};

export const isFeatureFlagEnabled = (
  courseSectionId: string,
  featureFlagsGrouped: FeatureFlagsGroupedDto[],
  featureFlag: FEATURE_FLAG,
  app: Application = Application.ASSIGNMENT_MANAGEMENT
) => {
  let specificAppFlags = featureFlagsGrouped;

  if (app !== Application.ASSIGNMENT_MANAGEMENT) {
    specificAppFlags = featureFlagsGrouped.filter(flag => flag.eolsApp === app);
  }

  return getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    specificAppFlags,
    featureFlag,
    courseSectionId
  );
};

export const isLearningCoachFeatureEnabled = (
  isbn: string,
  featureFlagsGrouped: FeatureFlagsGroupedDto[]
): boolean => {
  if (!isbn || !featureFlagsGrouped) {
    return false;
  }

  const isEaqFlagEnabled = isFeatureFlagEnabled(
    isbn,
    featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_ELS_LEARNING_COACH,
    Application.EAQ
  );

  if (typeof isEaqFlagEnabled === 'boolean') {
    return isEaqFlagEnabled;
  }

  return isFeatureFlagEnabled(
    isbn,
    featureFlagsGrouped.filter(flag => flag.eolsApp === Application.ASSIGNMENT_MANAGEMENT),
    FEATURE_FLAG.ENABLE_ELS_LEARNING_COACH,
    Application.ASSIGNMENT_MANAGEMENT
  ) === true;
};
